import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import useSubscribe from 'modules/core/hooks/useSubscribe'

import { useFinancialWorkspace } from '../../FinancialWorkspace/Providers/Provider'
import { InsightTableGroupBy, TableDataVariables } from '../../FinancialWorkspace/types/table'
import { useCachedData } from '../Providers/CachedDataProvider'
import { useCustomFilters } from '../Providers/CustomFilters'
import { useData } from '../Providers/DataProvider'
import { PAYROLL_SOURCES_EVENT } from '../components/Filters/DataSourceSelector/const'
import { useGroupedEmployeeCostData } from './useGroupedEmployeeCostData'
import { useEmployeeCostHeaders } from './useHeaders'

export const useEmployeeCostData = (variables: TableDataVariables) => {
  const { setData: setCachedData } = useCachedData()
  const { formattedState } = useFinancialWorkspace()
  const { formattedState: customFilters } = useCustomFilters()
  const { agreementData, expensesData, payrollData } = useData()
  const { headers } = useEmployeeCostHeaders()

  const groupBy = variables?.groupBy

  const isGrouped = useUnsafeMemo(() => groupBy !== InsightTableGroupBy.Total, [groupBy])

  useUnsafeEffect(() => {
    setCachedData({})
  }, [formattedState, customFilters, setCachedData])

  const {
    loading,
    pagination,
    data: groupedData,
    refetch: groupedDataRefetch,
  } = useGroupedEmployeeCostData(variables)

  const globalData = [
    ...(agreementData?.data ?? []),
    ...(payrollData?.data ?? []),
    ...(expensesData?.data ?? []),
  ].flat()

  const items = useUnsafeMemo(() => {
    if (isGrouped) {
      return groupedData
    }
    return globalData
  }, [groupedData, globalData, isGrouped])

  const handleRefetch = () => {
    if (isGrouped) {
      setCachedData({})
      variables.resetPagination?.()
      groupedDataRefetch()
    } else {
      payrollData?.refetch()
    }
  }

  useSubscribe([PAYROLL_SOURCES_EVENT], () => {
    handleRefetch()
  })

  return {
    items: items,
    headers: headers,
    refetch: handleRefetch,
    loading: isGrouped ? loading : false,
    hasNextPage: pagination?.hasNextPage ?? false,
    endCursor: pagination?.endCursor ?? undefined,
    showPagination: pagination?.hasNextPage ?? false,
    totalCount: Number(pagination?.totalCount) || items.length,
  }
}
